import { P2GBox, P2GButton, P2GContainer, P2GLink, P2GText } from '@parcel2go/core-ui';
import { Suspense, useContext } from 'react';
import { SmartSendContext } from '../../lib/context/SmartSendContext';
import { UpdateFilterAction } from '../../lib/context/SmartSendContextModels';
import { Consts, EditEventType } from '../../lib/em/EmitTypes';
import { useEventEmitter } from '../../lib/em/Hooks';
import { getTabbedNamedFilter } from '../../lib/helpers/DefaultFilters';
import { FilterName } from '../../lib/helpers/SmartSendFilterApplier';
import { EditType } from './item/EditType';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GLinkGroup } from '@parcel2go/core-ui';
import { TruncateString } from '../../lib/helpers/StringHelper';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import { P2GImageMessage } from '~/components/imagemessage';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import QuickCsv from '../onboarding/components/quick-csv';
import AccountIssues from './AccountIssues';
import useGoto from '~/hooks/use-goto';

interface props {
  filterKey: string;
}

const NoItems: React.FC<props> = (props) => {
  const { goTo } = useGoto();
  const ctx = useContext(SmartSendContext);
  const { emit } = useEventEmitter<EditEventType>();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const setAction = (a: EditType) => {
    emit(Consts.Edit, { Id: null, Action: a });
  };

  const update = (name: FilterName) => {
    const filter = getTabbedNamedFilter(name, ctx.state.filters[props.filterKey].filter.CurrentFilter);
    ctx.dispatch(
      new UpdateFilterAction(props.filterKey, { ...ctx.state.filters[props.filterKey].filter, CurrentFilter: filter })
    );
  };

  const resetSearchTerm = () => {
    ctx.dispatch(
      new UpdateFilterAction(props.filterKey, {
        ...ctx.state.filters[props.filterKey].filter,
        CurrentFilter: { ...ctx.state.filters[props.filterKey].filter.CurrentFilter, SearchTerm: '' }
      })
    );
  };

  if (ctx.state.filters[props.filterKey].filter.CurrentFilter.SearchTerm) {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/no-results.png"
        message={t('NO_RESULT_FALLBACK', { defaultValue: 'No results found' })}
        contentKey="image-smartsend-item-search-no-results"
      >
        <P2GText component="p">
          <Trans
            t={t}
            i18nKey="NO_ITEM_RESULT_ERROR"
            values={{
              searchTerm: TruncateString(ctx.state.filters[props.filterKey].filter.CurrentFilter.SearchTerm, 40)
            }}
            defaults="There are no results for <strong>{{searchTerm}}</strong>."
          />
        </P2GText>
        <P2GBox my={4}>
          <P2GLink onClick={resetSearchTerm}>{t('CLEAR_SEARCH_LINK', { defaultValue: 'Clear Search' })}</P2GLink>
        </P2GBox>
      </P2GImageMessage>
    );
  }
  switch (ctx.state.filters[props.filterKey].filter.CurrentFilter.Name) {
    case 'Ready':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/ready.png"
          message={t('NO_ITEMS_FALLBACK', { defaultValue: 'You have no items that are ready' })}
          contentKey="image-smartsend-items-ready-empty-list"
        >
          <P2GText component="p">
            {t('NO_ITEMS_DESCRIPTION', {
              defaultValue: 'You can view all the items that need fixing using the link below.'
            })}
          </P2GText>
          <P2GBox my={4}>
            <P2GLink
              onClick={() => {
                update('NotReady');
                goTo(RoutingConstants.SmartSend);
              }}
            >
              {t('VIEW_NOT_READY_LINK', { defaultValue: 'View Not Ready' })}
            </P2GLink>
          </P2GBox>
        </P2GImageMessage>
      );
    case 'SavedForLater':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/saved-for-later.png"
          message={t('NO_ITEMS_SAVED_FALLBACK', { defaultValue: 'You have no items saved for later' })}
          contentKey="image-smartsend-items-saved-for-later-empty-list"
        >
          <P2GText component="p">
            {t('NO_ITEMS_SAVED_DESCRIPTION', {
              defaultValue: 'You can view all the items that are ready using the link below.'
            })}
          </P2GText>
          <P2GBox my={4}>
            <P2GLink
              onClick={() => {
                update('Ready');
                goTo(RoutingConstants.SmartSend);
              }}
            >
              {t('VIEW_READY_LINK', { defaultValue: 'View Ready' })}
            </P2GLink>
          </P2GBox>
        </P2GImageMessage>
      );
    case 'NotReady':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/not-ready.png"
          message={t('NO_ITEMS_NEED_FIXING_FALLBACK', { defaultValue: 'You have no items which need fixing' })}
          contentKey="image-smartsend-items-not-ready-empty-list"
        >
          <P2GText component="p">
            {t('NO_ITEMS_NEED_FIXING_DESCRIPTION', {
              defaultValue: 'You can view all the items that are ready using the link below.'
            })}
          </P2GText>
          <P2GBox my={4}>
            <P2GLink
              onClick={() => {
                update('Ready');
                goTo(RoutingConstants.SmartSend);
              }}
            >
              {t('VIEW_READY_LINK', { defaultValue: 'View Ready' })}
            </P2GLink>
          </P2GBox>
        </P2GImageMessage>
      );
    case 'Issues':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/not-ready.png"
          message={t('NO_ITEMS_NEED_FIXING_FALLBACK', { defaultValue: `You've fixed all these items` })}
          contentKey="image-smartsend-items-not-ready-empty-list"
        >
          <P2GText component="p">
            {t('NO_ITEMS_NEED_FIXING_DESCRIPTION', {
              defaultValue: 'You can view all the items that are ready using the link below.'
            })}
          </P2GText>
          <P2GBox my={4}>
            <P2GButton
              size="large"
              variant="primary"
              onClick={() => {
                update('DEFAULT');
                goTo(RoutingConstants.SmartSend);
              }}
            >
              {t('ISSUES_RESOLVED_CONTINUE_BUTTON', { defaultValue: 'Continue' })}
            </P2GButton>
          </P2GBox>
        </P2GImageMessage>
      );
    case 'Groupable':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/groupable.png"
          message={t('NO_ITEMS_TO_GROUP_FALLBACK', { defaultValue: 'You have no items that can be grouped' })}
          contentKey="image-smartsend-items-groupable-empty-list"
        >
          <P2GBox my={4}>
            <P2GLink
              onClick={() => {
                update('DEFAULT');
                goTo(RoutingConstants.SmartSend);
              }}
            >
              {t('VIEW_ALL_LINK', { defaultValue: 'View All' })}
            </P2GLink>
          </P2GBox>
        </P2GImageMessage>
      );
    default:
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/no-orders.png"
          message={t('NO_NEW_MARKETPLACE_FALLBACK', { defaultValue: 'You have no new marketplace orders' })}
          contentKey="image-smartsend-items-all-empty-list"
        >
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_NEW_MARKETPLACE_DESCRIPTION"
              components={{ a: <P2GLink onClick={() => goTo(RoutingConstants.OrderHistory)} /> }}
              defaults="Marketplace orders you have arranged delivery for can be found in <a>Order History</a>."
            />
          </P2GText>

          <Suspense>
            <AccountIssues
              wrapper={(x) => (
                <P2GBox display="flex" justifyContent="center" mt={4}>
                  {x}
                </P2GBox>
              )}
            />
          </Suspense>

          {SettingsCache.settings.Features.includes('CsvUpload') && (
            <P2GBox mt={4}>
              <P2GContainer maxWidth="xs">
                <Suspense>
                  <QuickCsv />
                  {SettingsCache.settings.Features.includes('CsvUploadExamples') && (
                    <P2GBox mt={1}>
                      <P2GLink variant="body2" onClick={() => setAction('CSV')}>
                        {t('UPLOAD_CSV_EXAMPLE_FILES', { defaultValue: 'View Example Files' })}
                      </P2GLink>
                    </P2GBox>
                  )}
                </Suspense>
              </P2GContainer>
            </P2GBox>
          )}
          <P2GBox my={4}>
            <P2GLinkGroup
              links={[
                <P2GLink
                  key="add-manual-item"
                  onClick={() => {
                    setAction('CREATE');
                    goTo(RoutingConstants.SmartSend);
                  }}
                >
                  {t('ADD_MANUAL_ITEM_LINK', { defaultValue: 'Add Manual Order' })}
                </P2GLink>,
                <P2GLink key="link-account" onClick={() => goTo(RoutingConstants.AccountLinking)}>
                  {t('LINK_ACCOUNT', { defaultValue: 'Link Account' })}
                </P2GLink>
              ]}
            />
          </P2GBox>
        </P2GImageMessage>
      );
  }
};

export default NoItems;
